<!-- Loading Spinner -->
<div *ngIf="loading" class="flex justify-center items-center min-h-screen">
  <div
    class="animate-spin rounded-full h-16 w-16 border-b-2 border-arabic-green"
  ></div>
</div>

<!-- Error Message -->
<div
  *ngIf="error"
  class="p-6 bg-gray-100 dark:bg-gray-800 min-h-screen font-sans"
>
  <div
    class="bg-red-50 border border-red-200 text-red-800 rounded-xl p-6 shadow-lg mx-auto max-w-2xl"
  >
    <h3 class="text-xl font-semibold mb-3">
      {{ "GRAVE_DETAILS.ERROR" | translate }}
    </h3>
    <p>{{ error }}</p>
    <button
      class="mt-4 px-4 py-2 bg-arabic-green text-white rounded-lg hover:bg-arabic-green-dark transition-colors"
      (click)="goBack()"
    >
      {{ "GRAVE_DETAILS.BACK_TO_SEARCH" | translate }}
    </button>
  </div>
</div>

<!-- Image Viewer Modal -->
<div
  *ngIf="isImageModalOpen"
  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-80 backdrop-blur-sm transition-opacity"
  (click)="isImageModalOpen = false"
  (keydown.escape)="isImageModalOpen = false"
  tabindex="0"
>
  <div
    class="relative max-w-4xl w-full h-full flex flex-col items-center justify-center"
    (click)="$event.stopPropagation()"
  >
    <!-- Close button -->
    <button
      (click)="isImageModalOpen = false"
      class="absolute top-4 right-4 z-10 p-2 bg-white cursor-pointer dark:bg-gray-700 text-gray-800 dark:text-white rounded-full shadow-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
      aria-label="Close image viewer"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>

    <!-- Image -->
    <div class="w-full h-full flex items-center justify-center overflow-hidden">
      <img
        *ngIf="modalImageSrc"
        [src]="modalImageSrc"
        [alt]="modalImageAlt"
        class="max-h-full max-w-full object-contain"
      />
    </div>
  </div>
</div>

<!-- Grave Details -->
<div
  class="p-6 bg-gray-100 dark:bg-gray-800 min-h-screen"
  *ngIf="grave && !loading"
>
  <!-- Header Section -->
  <div class="mb-8">
    <div class="text-gray-500 dark:text-gray-400 text-sm mb-2">
      <span>{{ "GRAVE_DETAILS.TITLE" | translate }}</span> /
      <span>{{ grave.refID }}</span>
    </div>
    <!-- Arabic language - show Arabic name as h1 and English as h2 -->
    <ng-container *ngIf="translate.currentLang === 'ar'">
      <h1 class="text-4xl font-bold text-gray-800 dark:text-gray-100 m-0">
        {{ grave.fullNameAr }}
      </h1>
      <h2
        class="text-2xl font-medium text-gray-600 dark:text-gray-300 mt-2 mb-0"
      >
        {{ grave.fullNameEn }}
      </h2>
    </ng-container>
    <!-- English language - show English name as h1 and Arabic as h2 -->
    <ng-container *ngIf="translate.currentLang !== 'ar'">
      <h1 class="text-4xl font-bold text-gray-800 dark:text-gray-100 m-0">
        {{ grave.fullNameEn }}
      </h1>
      <h2
        class="text-2xl font-medium text-gray-600 dark:text-gray-300 mt-2 mb-0"
      >
        {{ grave.fullNameAr }}
      </h2>
    </ng-container>
  </div>

  <!-- Content Grid -->
  <div class="grid grid-cols-1 gap-6">
    <!-- Top row - Image and Map side by side -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <!-- Image Card -->
      <div class="lg:col-span-4 mb-6 lg:mb-0">
        <div
          class="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 h-full flex flex-col"
        >
          <div
            class="text-xl font-semibold text-gray-800 dark:text-gray-200 p-4 pb-2 border-b border-gray-100 dark:border-gray-600"
          >
            {{ "GRAVE_DETAILS.PHOTO" | translate }}
          </div>
          <div class="p-4 flex-grow flex items-center justify-center">
            <div
              *ngIf="!grave.imageURLBase64"
              class="h-48 w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-20 w-20 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <!-- Clickable image with expand hint -->
            <div
              *ngIf="grave.imageURLBase64"
              (click)="openImageModal('grave')"
              class="relative cursor-pointer group overflow-hidden rounded-lg"
              tabindex="0"
              role="button"
              aria-label="View larger image"
              (keydown.enter)="openImageModal('grave')"
            >
              <img
                [src]="'data:image/jpg;base64,' + grave.imageURLBase64"
                [alt]="
                  translate.currentLang === 'ar'
                    ? grave.fullNameAr
                    : grave.fullNameEn
                "
                class="max-w-full max-h-[500px] object-contain rounded-lg transition-transform group-hover:scale-105"
              />
              <!-- Expand icon overlay -->
              <div
                class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black bg-opacity-40 transition-opacity rounded-lg"
              >
                <div
                  class="bg-white bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90 p-2 rounded-full"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 text-gray-800 dark:text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Card -->
      <div class="lg:col-span-8">
        <div
          class="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 h-full"
        >
          <div class="p-6">
            <h3
              class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-5 pb-3 border-b border-gray-100 dark:border-gray-600"
            >
              {{ "GRAVE_DETAILS.LOCATION" | translate }}
            </h3>
            <div class="rounded-lg overflow-hidden mb-4">
              <!-- No coordinates available message -->
              <div
                *ngIf="!grave.latitude || !grave.longitude"
                class="bg-yellow-50 dark:bg-yellow-900/30 p-4 text-center text-yellow-700 dark:text-yellow-500 rounded-lg"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p>{{ "GRAVE_DETAILS.LOCATION_UNAVAILABLE" | translate }}</p>
              </div>

              <!-- Server-side rendering placeholder -->
              <div
                *ngIf="!isBrowser && grave.latitude && grave.longitude"
                class="h-[400px] bg-gray-100 dark:bg-gray-800 flex items-center justify-center rounded-lg"
              >
                <div class="text-center p-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <p class="text-gray-500 dark:text-gray-400">
                    {{ "GRAVE_DETAILS.LOCATION" | translate }}
                  </p>
                </div>
              </div>

              <!-- API not loaded message (only shown in browser) -->
              <div
                *ngIf="
                  isBrowser && grave.latitude && grave.longitude && !apiLoaded
                "
                class="flex justify-center items-center h-[400px] bg-gray-100 dark:bg-gray-800 rounded-lg"
              >
                <div class="text-center">
                  <div
                    class="animate-spin rounded-full h-10 w-10 border-b-2 border-arabic-green mx-auto mb-4"
                  ></div>
                  <p class="text-gray-500 dark:text-gray-400">
                    {{ "GRAVE_DETAILS.LOADING_MAP" | translate }}
                  </p>
                </div>
              </div>

              <!-- Google Map (only shown in browser when API is loaded) -->
              <div
                *ngIf="
                  isBrowser && grave.latitude && grave.longitude && apiLoaded
                "
                class="map-container"
                style="
                  height: 400px;
                  width: 100%;
                  border-radius: 0.5rem;
                  overflow: hidden;
                "
              >
                <google-map
                  [center]="center"
                  [zoom]="zoom"
                  [options]="mapOptions"
                  height="400px"
                  width="100%"
                >
                  <map-advanced-marker
                    [position]="markerPosition"
                    [title]="
                      translate.currentLang === 'ar'
                        ? grave.fullNameAr
                        : grave.fullNameEn
                    "
                  >
                  </map-advanced-marker>
                </google-map>
              </div>
            </div>
            <div
              class="flex justify-between mt-4"
              *ngIf="grave.latitude && grave.longitude"
            >
              <div class="flex flex-col">
                <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                  "GRAVE_DETAILS.LATITUDE" | translate
                }}</span>
                <span
                  class="text-base font-medium text-gray-700 dark:text-gray-300"
                  >{{ grave.latitude }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                  "GRAVE_DETAILS.LONGITUDE" | translate
                }}</span>
                <span
                  class="text-base font-medium text-gray-700 dark:text-gray-300"
                  >{{ grave.longitude }}</span
                >
              </div>
            </div>

            <!-- AR Navigation Button -->
            <div
              *ngIf="grave.latitude && grave.longitude && isBrowser"
              class="mt-4"
            >
              <button
                class="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                (click)="navigateToGoogleMaps()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 20l-5.447-2.724A2 2 0 013 15.382V6a2 2 0 012-2h14a2 2 0 012 2v9.382a2 2 0 01-1.553 1.894L15 20v-2a2 2 0 00-2-2h-2a2 2 0 00-2 2v2z"
                  />
                </svg>
                <span>{{
                  "GRAVE_DETAILS.NAVIGATE_WITH_GOOGLE_MAPS" | translate
                }}</span>
              </button>
              <p
                class="text-xs text-center text-gray-500 dark:text-gray-400 mt-2"
              >
                {{ "GRAVE_DETAILS.NAVIGATION_NOTE" | translate }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info and Description Section -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <!-- Personal Info Card -->
      <div class="lg:col-span-6">
        <div
          class="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 p-6 h-full"
        >
          <h3
            class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-5 pb-3 border-b border-gray-100 dark:border-gray-600"
          >
            {{ "GRAVE_DETAILS.PERSONAL_INFO" | translate }}
          </h3>
          <div class="grid grid-cols-1 gap-4">
            <div class="flex flex-col">
              <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                "GRAVE_DETAILS.GRAVE_NUMBER" | translate
              }}</span>
              <span
                class="text-base font-medium text-gray-700 dark:text-gray-300"
                >{{ grave.refID }}</span
              >
            </div>
            <div class="flex flex-col" *ngIf="grave.dateOfBirth">
              <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                "GRAVE_DETAILS.DATE_OF_BIRTH" | translate
              }}</span>
              <span
                class="text-base font-medium text-gray-700 dark:text-gray-300"
                >{{ grave.dateOfBirth | date }}</span
              >
            </div>
            <div class="flex flex-col" *ngIf="grave.dateOfDeath">
              <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                "GRAVE_DETAILS.DATE_OF_DEATH" | translate
              }}</span>
              <span
                class="text-base font-medium text-gray-700 dark:text-gray-300"
                >{{ grave.dateOfDeath | date }}</span
              >
            </div>
            <div class="flex flex-col" *ngIf="grave.zGender">
              <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                "GRAVE_DETAILS.GENDER" | translate
              }}</span>
              <span
                class="text-base font-medium text-gray-700 dark:text-gray-300"
              >
                {{
                  translate.currentLang === "en"
                    ? grave.zGender?.nameEn
                    : grave.zGender?.nameAr
                }}
              </span>
            </div>
            <div class="flex flex-col" *ngIf="grave.zZone">
              <span class="text-sm text-gray-500 dark:text-gray-400 mb-1">{{
                "GRAVE_DETAILS.ZONE" | translate
              }}</span>
              <span
                class="text-base font-medium text-gray-700 dark:text-gray-300"
              >
                {{
                  translate.currentLang === "en"
                    ? grave.zZone?.nameEn
                    : grave.zZone?.nameAr
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Description Card -->
      <div class="lg:col-span-6">
        @if ((grave.description && translate.currentLang === 'ar') ||
        (grave.descriptionEn && translate.currentLang !== 'ar')) {
        <div
          class="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 p-6 h-full"
        >
          <h3
            class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-5 pb-3 border-b border-gray-100 dark:border-gray-600"
          >
            {{ "GRAVE_DETAILS.DESCRIPTION" | translate }}
          </h3>
          <p class="text-gray-700 dark:text-gray-300 leading-relaxed">
            {{
              translate.currentLang === "ar"
                ? grave.description
                : grave.descriptionEn
            }}
          </p>
        </div>
        }
      </div>
    </div>
  </div>

  <!-- Death Certificate & Body Certificate Section (Admin/User only) -->
  <ng-container *appHasRole="['Admin', 'User']">
    <div
      class="mt-8"
      *ngIf="
        grave.deathCertificateImageURLBase64 ||
        grave.bodyReceiptCertificateImageURLBase64
      "
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Death Certificate Card -->
        <div
          class="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6"
          *ngIf="grave.deathCertificateImageURLBase64"
        >
          <h3
            class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 pb-2 border-b border-gray-100 dark:border-gray-600"
          >
            {{ "GRAVE_DETAILS.DEATH_CERTIFICATE" | translate }}
          </h3>
          <div class="text-gray-700 dark:text-gray-300">
            <ng-container
              *ngIf="
                grave.deathCertificateImageURLBase64 != null;
                else noDeathCert
              "
            >
              <img
                [src]="
                  'data:image/jpeg;base64,' +
                  grave.deathCertificateImageURLBase64
                "
                alt="Death Certificate"
                class="max-w-full rounded shadow cursor-pointer"
                (click)="openImageModal('death')"
                tabindex="0"
                role="button"
                aria-label="View larger image"
                (keydown.enter)="openImageModal('death')"
              />
            </ng-container>
            <ng-template #noDeathCert>
              <span class="italic text-gray-400">{{
                "GRAVE_DETAILS.NO_DEATH_CERTIFICATE" | translate
              }}</span>
            </ng-template>
          </div>
        </div>
        <!-- Body Certificate Card -->
        <div
          class="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6"
          *ngIf="grave.bodyReceiptCertificateImageURLBase64"
        >
          <h3
            class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 pb-2 border-b border-gray-100 dark:border-gray-600"
          >
            {{ "GRAVE_DETAILS.BODY_CERTIFICATE" | translate }}
          </h3>
          <div class="text-gray-700 dark:text-gray-300">
            <ng-container
              *ngIf="
                grave.bodyReceiptCertificateImageURLBase64 != null;
                else noBodyCert
              "
            >
              <img
                [src]="
                  'data:image/jpeg;base64,' +
                  grave.bodyReceiptCertificateImageURLBase64
                "
                alt="Body Certificate"
                class="max-w-full rounded shadow cursor-pointer"
                (click)="openImageModal('body')"
                tabindex="0"
                role="button"
                aria-label="View larger image"
                (keydown.enter)="openImageModal('body')"
              />
            </ng-container>
            <ng-template #noBodyCert>
              <span class="italic text-gray-400">{{
                "GRAVE_DETAILS.NO_BODY_CERTIFICATE" | translate
              }}</span>
            </ng-template>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Back button -->
  <div class="mt-6 flex justify-between items-center">
    <button
      class="px-6 py-3 bg-arabic-green text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center cursor-pointer"
      (click)="goBack()"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-5 w-5 mr-2 rtl:rotate-180"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10 19l-7-7m0 0l7-7m-7 7h18"
        />
      </svg>
      {{ "GRAVE_DETAILS.BACK_TO_SEARCH" | translate }}
    </button>

    <!-- Suggestion button -->
    <button
      class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center cursor-pointer"
      (click)="openSuggestionModal()"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-5 w-5 mr-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
      {{ "GRAVE_DETAILS.SUGGEST_CHANGES" | translate }}
    </button>
  </div>
</div>

<!-- Suggestion Modal -->
<div
  *ngIf="isSuggestionModalOpen"
  class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
  (keydown.escape)="isSuggestionModalOpen = false"
  tabindex="0"
>
  <div
    class="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-lg w-full transform transition-all"
    [class.scale-100]="isSuggestionModalOpen"
    [class.scale-95]="!isSuggestionModalOpen"
    [class.opacity-100]="isSuggestionModalOpen"
    [class.opacity-0]="!isSuggestionModalOpen"
  >
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-semibold text-gray-800 dark:text-gray-100">
        {{ "GRAVE_DETAILS.SUGGEST_CHANGES_TITLE" | translate }}
      </h3>
      <button
        (click)="isSuggestionModalOpen = false"
        class="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition-colors"
        aria-label="Close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Description -->
    <p class="text-gray-600 dark:text-gray-300 mb-4">
      {{ "GRAVE_DETAILS.SUGGESTION_DESCRIPTION" | translate }}
    </p>

    <!-- Form -->
    <div>
      <form (ngSubmit)="submitSuggestion()" [formGroup]="suggestionForm">
        <div class="mb-4">
          <app-text-area
            formControlName="additionalMessage"
            [label]="'GRAVE_DETAILS.SUGGESTION_MESSAGE' | translate"
            [placeholder]="'GRAVE_DETAILS.SUGGESTION_PLACEHOLDER' | translate"
            [rows]="5"
            [required]="true"
            [showError]="submitted"
          ></app-text-area>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            (click)="isSuggestionModalOpen = false"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {{ "COMMON.CANCEL" | translate }}
          </button>
          <button
            type="submit"
            [disabled]="isSubmitting || suggestionForm.invalid"
            class="px-4 py-2 bg-arabic-green text-white rounded-lg hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arabic-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <span
              *ngIf="isSubmitting"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </span>
            {{ "GRAVE_DETAILS.SEND_SUGGESTION" | translate }}
          </button>
        </div>
      </form>
    </div>
  </div>
</div>

<!-- Success Message Toast -->
<div
  *ngIf="showSuccessToast"
  class="fixed bottom-4 left-0 right-0 mx-auto w-auto max-w-md bg-green-50 border-l-4 border-green-500 text-green-800 p-4 rounded shadow-md transition-opacity duration-500 z-50"
  [class.opacity-100]="showSuccessToast"
  [class.opacity-0]="!showSuccessToast"
>
  <div class="flex items-center">
    <div class="py-1 mr-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
    </div>
    <div>
      <p class="font-medium text-green-800">
        {{ "GRAVE_DETAILS.SUGGESTION_SENT" | translate }}
      </p>
      <p class="text-sm text-green-700">
        {{ "GRAVE_DETAILS.SUGGESTION_THANK_YOU" | translate }}
      </p>
    </div>
  </div>
</div>

<!-- Error Message Toast -->
<div
  *ngIf="showErrorToast"
  class="fixed bottom-4 left-0 right-0 mx-auto w-auto max-w-md bg-red-50 border-l-4 border-red-500 text-red-800 p-4 rounded shadow-md transition-opacity duration-500 z-50"
  [class.opacity-100]="showErrorToast"
  [class.opacity-0]="!showErrorToast"
>
  <div class="flex items-center">
    <div class="py-1 mr-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 text-red-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    </div>
    <div>
      <p class="font-medium text-red-800">
        {{ "GRAVE_DETAILS.SUGGESTION_ERROR" | translate }}
      </p>
      <p class="text-sm text-red-700">
        {{ "GRAVE_DETAILS.SUGGESTION_TRY_AGAIN" | translate }}
      </p>
    </div>
  </div>
</div>
